import 'src/styles/reset.css';
import 'src/styles/fonts.css';
import 'src/styles/global.css';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import { BentoProvider, lightTheme } from '@robinhood/design';
import * as Sentry from '@sentry/react';
import { AppProps } from 'next/app';
import Head from 'next/head';

import { ApiEnv } from 'src/clients/Network/http/fetch';
import RelayEnvironment from 'src/clients/relay/RelayEnvironment';
import useAmplitude from 'src/hooks/useAmplitude';

const SENTRY_ALLOWED_ENVS = new Set(['production', 'staging']);
const SENTRY_DSN = 'https://5ad54aecc1ab698b52c91d92b57dd876@o115947.ingest.sentry.io/4505994274930688';
const DATADOG_CLIENT_TOKEN = 'pubfe18db57a8afcfabf4f023ef99ba436a';
const DATADOG_SAMPLING_RATE = 10;

Sentry.init({
  enabled: SENTRY_ALLOWED_ENVS.has(ApiEnv),
  environment: ApiEnv,
  dsn: SENTRY_DSN,
  denyUrls: [/googleadservices\.com/, /connect\.facebook\.net/],
});

datadogRum.init({
  applicationId: '4e363f31-9ba2-49d2-98c5-6a7d86f4b4ad',
  clientToken: DATADOG_CLIENT_TOKEN,
  site: 'datadoghq.com',
  service: 'rh-card-web',
  env: ApiEnv === 'production' ? 'prod' : 'staging',

  // version: '1.0.0',
  trackUserInteractions: true,
  sessionSampleRate: DATADOG_SAMPLING_RATE,
  sessionReplaySampleRate: 0,
  defaultPrivacyLevel: 'mask-user-input',
  plugins: [reactPlugin({ router: false })],
  allowedTracingUrls: [
    // Robinhood CORS policy only allows tracecontext propagator
    { match: 'https://api.robinhood.com/creditcard', propagatorTypes: ['tracecontext'] },
    { match: /^https:\/\/[^\/]+\.x1creditcard\.com/, propagatorTypes: ['tracecontext', 'datadog'] },
  ],
});

datadogLogs.init({
  clientToken: DATADOG_CLIENT_TOKEN,
  site: 'datadoghq.com',
  forwardErrorsToLogs: true,
  sessionSampleRate: DATADOG_SAMPLING_RATE,
});

export default function App({ Component, pageProps }: AppProps) {
  useAmplitude();

  return (
    <>
      <Head>
        <meta name='viewport' content='width=device-width, initial-scale=1.0, viewport-fit=cover, user-scalable=no' />
        <meta name='robots' content='noindex' />
        <meta name='theme-color' content='#000000' />
        <meta key='og-type' property='og:type' content='website' />
      </Head>

      <BentoProvider defaultToastDuration={5000} linkComponent={null} theme={lightTheme}>
        {({ rootClassName: bentoClass }) => (
          <div className={bentoClass}>
            <RelayEnvironment>
              <Component {...pageProps} />
            </RelayEnvironment>
          </div>
        )}
      </BentoProvider>
    </>
  );
}
